import { attrToAutofillField, defaultTypeForAttr, partyLabel, } from './vocabulary.js';
function resolveSlotProjection(metadata, proj) {
    const slot = metadata.partySlots.find((s) => s.id === proj.slot);
    if (!slot) {
        throw new Error(`partyProjection references unknown slot "${proj.slot}" — ` +
            `declare it in metadata.partySlots first.`);
    }
    const suffix = proj.labelSuffix ?? '';
    return proj.fields.map((f) => ({
        key: f.key,
        // The per-slot suffix (e.g. " (medhyresgäst)") applies to the resolved
        // label whether it came from the vocabulary or a per-field override.
        label: (f.label ?? partyLabel(f.attr, proj.idHint)) + suffix,
        type: f.type ?? defaultTypeForAttr(f.attr),
        field: attrToAutofillField(f.attr),
        role: slot.role,
        ordinal: slot.ordinal,
        section: proj.section,
        help: f.help,
        requiredAtFinal: f.requiredAtFinal,
        requiredWhen: f.requiredWhen,
        visibleWhen: f.visibleWhen,
        scopes: f.scopes,
    }));
}
/**
 * Resolved party fields for a single slot id — what `<PartyBlock>` renders
 * and a subset of what the editor shows. Returns `[]` if the template has
 * no projection or no entry for that slot.
 */
export function projectedFieldsForSlot(metadata, slotId) {
    const proj = metadata.partyProjection?.find((p) => p.slot === slotId);
    return proj ? resolveSlotProjection(metadata, proj) : [];
}
function toVariableFields(resolved) {
    return resolved.map((rf) => ({
        key: rf.key,
        label: rf.label,
        type: rf.type,
        section: rf.section,
        autofill: {
            kind: 'party',
            role: rf.role,
            ordinal: rf.ordinal,
            field: rf.field,
        },
        ...(rf.help !== undefined ? { help: rf.help } : {}),
        ...(rf.requiredAtFinal !== undefined
            ? { requiredAtFinal: rf.requiredAtFinal }
            : {}),
        ...(rf.requiredWhen !== undefined ? { requiredWhen: rf.requiredWhen } : {}),
        ...(rf.visibleWhen !== undefined ? { visibleWhen: rf.visibleWhen } : {}),
        ...(rf.scopes !== undefined ? { scopes: rf.scopes } : {}),
    }));
}
/**
 * Compile a SINGLE slot's projection into `VariableField[]`. Use when a
 * template's party fields are non-contiguous in `variableSchema` (e.g.
 * property fields sit between the lessor and lessee blocks) so each block
 * can be spread in place, preserving field order.
 */
export function expandPartySlot(metadata, slotId) {
    return toVariableFields(projectedFieldsForSlot(metadata, slotId));
}
/**
 * Compile the whole `partyProjection` into `VariableField[]`, in
 * declaration order (slot order, then field order). Spread into the
 * template's exported `variableSchema` where its party fields used to be.
 */
export function expandPartyProjection(metadata) {
    const out = [];
    for (const proj of metadata.partyProjection ?? []) {
        out.push(...toVariableFields(resolveSlotProjection(metadata, proj)));
    }
    return out;
}
//# sourceMappingURL=expand.js.map